import { Injectable } from '@angular/core';
import { safeLocalStorage } from './safe-web-storage';

/**
 * Double-stringify wire format for `__auth_customer_storage__`. The only
 * expression in the codebase that knows the format: both the pre-bootstrap
 * handoff writer (no Angular DI available yet) and
 * `CustomerAuthService.saveState` go through {@link writeSavedState}, so the
 * two paths cannot drift apart.
 */
function encodeSavedState(data: unknown): string {
  return JSON.stringify(JSON.stringify(data));
}

/**
 * The single writer for the double-encoded wire format, usable without DI so
 * the pre-bootstrap handoff and the service share one implementation.
 * @returns false if localStorage refused the write (quota / disabled / site
 *          data blocked). The value is then still readable for the rest of
 *          this page through the crash-safe helper (card #11603), but it will
 *          not survive a reload.
 */
export function writeSavedState(
  localStorageKey: string,
  data: unknown,
): boolean {
  return safeLocalStorage.setItem(localStorageKey, encodeSavedState(data));
}

/**
 * The counterpart of {@link writeSavedState}: the only expression that knows how
 * to read the double-encoded wire format back. Usable without DI for the same
 * reason the writer is — the pre-bootstrap handoff runs before Angular exists.
 *
 * Tolerates a single-encoded object, array, number or boolean, so such a key
 * written by the older `setSavedState(state, key)` convention still reads back.
 * A single-encoded string does not: it is taken for the double-encoded form and
 * parsed again, so it reads back as null (or as the JSON it happens to hold).
 *
 * @returns the decoded value, or null when the key is absent or the content is
 *          not valid JSON. When the browser refuses storage, a value written
 *          earlier on this page is read from the crash-safe helper's in-memory
 *          copy (card #11603).
 */
export function readSavedState(localStorageKey: string): unknown {
  const raw = safeLocalStorage.getItem(localStorageKey);
  if (!raw) {
    return null;
  }
  try {
    const decoded = JSON.parse(raw);
    return typeof decoded === 'string' ? JSON.parse(decoded) : decoded;
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  constructor() {}

  /**
   * Writes `data` in the double-encoded wire format — delegates to the shared
   * {@link writeSavedState} so the format lives in exactly one place.
   *
   * Note the argument order: `(key, data)`, the opposite of the older
   * {@link setSavedState}`(state, key)`. Read back with {@link readSavedState},
   * never with {@link getSavedState}, which decodes one level only.
   */
  writeSavedState(localStorageKey: string, data: unknown): boolean {
    return writeSavedState(localStorageKey, data);
  }

  /** Reads a key written by {@link writeSavedState} back. */
  readSavedState(localStorageKey: string): unknown {
    return readSavedState(localStorageKey);
  }

  /**
   * Single-encoded writer, kept for its existing callers.
   *
   * @deprecated Prefer {@link writeSavedState} / {@link readSavedState}. The two
   * writers take their arguments in opposite orders — `setSavedState(state,
   * key)` here versus `writeSavedState(key, data)` — and both parameters are
   * loosely typed, so calling the wrong one, or the right one with the arguments
   * swapped, compiles and silently writes a payload the matching reader cannot
   * decode. New call sites should use the pair above.
   *
   * @returns false if localStorage refused the write (quota / disabled / site
   * data blocked); the value then stays readable for this page only (card
   * #11603). Callers that ignore the return keep prior behaviour.
   */
  setSavedState(state: any, localStorageKey: string): boolean {
    return safeLocalStorage.setItem(localStorageKey, JSON.stringify(state));
  }

  removeSavedState(localStorageKey: string) {
    safeLocalStorage.removeItem(localStorageKey);
  }

  /**
   * Single-encoded reader, the counterpart of {@link setSavedState}. For a key
   * written by {@link writeSavedState} use {@link readSavedState} instead: this
   * one returns the inner JSON *string*, not the object.
   */
  getSavedState(localStorageKey: string): any {
    // Card #11603: reading storage can no longer throw, but a value written by
    // an older build can still be invalid JSON, so the parse stays guarded.
    // Falling back to the in-memory overlay (instead of returning nothing) is
    // what keeps a signed-in customer signed in for the visit when the browser
    // refuses storage.
    const raw = safeLocalStorage.getItem(localStorageKey);
    if (raw === null) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
}
